import React, { useEffect, useRef, useCallback, useState } from 'react';
import * as Three from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { ViewHelper } from 'three/examples/jsm/helpers/ViewHelper.js'; // Import ViewHelper

const ScenePreview = ({ experienceId, assets }) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingError, setLoadingError] = useState(null);
  const canvasRef = useRef(null);
  const viewHelperContainerRef = useRef(null); // Ref for ViewHelper container
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const viewHelperRendererRef = useRef(null); // Ref for ViewHelper renderer
  const viewHelperRef = useRef(null); // Ref for ViewHelper instance
  const controlsRef = useRef(null);
  const cameraRef = useRef(null);
  const animationIdRef = useRef(null);
  const loadersRef = useRef({});

  // Cleanup function
  const cleanup = useCallback(() => {
    // Cancel animation loop
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }

    // Dispose controls
    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }

    // Dispose ViewHelper renderer
    if (viewHelperRendererRef.current) {
      viewHelperRendererRef.current.dispose();
      viewHelperRendererRef.current.forceContextLoss();
      if (
        viewHelperContainerRef.current &&
        viewHelperRendererRef.current.domElement.parentNode ===
          viewHelperContainerRef.current
      ) {
        viewHelperContainerRef.current.removeChild(
          viewHelperRendererRef.current.domElement
        );
      }
      viewHelperRendererRef.current = null;
    }

    // Dispose scene objects
    if (sceneRef.current) {
      sceneRef.current.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => {
              Object.keys(material).forEach((key) => {
                if (material[key] && material[key].isTexture) {
                  material[key].dispose();
                }
              });
              material.dispose();
            });
          } else {
            Object.keys(object.material).forEach((key) => {
              if (object.material[key] && object.material[key].isTexture) {
                object.material[key].dispose();
              }
            });
            object.material.dispose();
          }
        }
      });
      sceneRef.current.clear();
      sceneRef.current = null;
    }

    // Dispose renderer
    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current.forceContextLoss();
      if (
        canvasRef.current &&
        rendererRef.current.domElement.parentNode === canvasRef.current
      ) {
        canvasRef.current.removeChild(rendererRef.current.domElement);
      }
      rendererRef.current = null;
    }

    // Clear loaders
    if (loadersRef.current.dracoLoader) {
      loadersRef.current.dracoLoader.dispose();
    }
    loadersRef.current = {};

    cameraRef.current = null;
    viewHelperRef.current = null;

    // Force garbage collection of large objects
    if (window.gc) {
      window.gc();
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !viewHelperContainerRef.current) return;

    // Cleanup any existing scene first
    cleanup();
    setLoadingProgress(0);
    setLoadingError(null);

    // Clear the canvas container completely
    while (canvasRef.current.firstChild) {
      canvasRef.current.removeChild(canvasRef.current.firstChild);
    }

    // Initialize scene
    const scene = new Three.Scene();
    scene.background = new Three.Color(0x2d2e32);
    sceneRef.current = scene;

    // Initialize camera
    const camera = new Three.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(5, 10, 15);
    cameraRef.current = camera;

    // Initialize renderer with proper settings
    const renderer = new Three.WebGLRenderer({
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = Three.SRGBColorSpace;
    renderer.toneMapping = Three.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    rendererRef.current = renderer;

    canvasRef.current.appendChild(renderer.domElement);

    // Add grid helper
    const gridHelper = new Three.GridHelper(50, 50, 0x645c5c, 0x645c5c);

    scene.add(gridHelper);

    // Add orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, 0, 0);
    controls.update();
    controlsRef.current = controls;

    // Add ambient light
    const ambientLight = new Three.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    // Add directional light
    const directionalLight = new Three.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    // Load environment map with proper error handling
    const rgbeLoader = new RGBELoader();
    rgbeLoader.load(
      '/hdr/brown_photostudio_02_2k.hdr',
      (envMap) => {
        if (sceneRef.current) {
          envMap.mapping = Three.EquirectangularReflectionMapping;
          sceneRef.current.environment = envMap;
        }
      },
      undefined,
      (error) => {
        console.warn('Failed to load HDR environment map:', error);
      }
    );

    // Initialize ViewHelper
    const viewHelperRenderer = new Three.WebGLRenderer({ alpha: true });
    viewHelperRenderer.setSize(90, 90); // Size of the ViewHelper widget
    viewHelperRendererRef.current = viewHelperRenderer;
    viewHelperContainerRef.current.appendChild(viewHelperRenderer.domElement);

    const viewHelper = new ViewHelper(camera, viewHelperRenderer.domElement);
    viewHelper.controls = controls;
    viewHelper.scale.set(0.67, 0.67, 0.67);
    viewHelper.position.set(1.2, -0.6, 0); // Scale the ViewHelper widget
    viewHelper.controls.center = controls.target;
    viewHelperRef.current = viewHelper;

    // Add click handler for ViewHelper
    viewHelperContainerRef.current.addEventListener('pointerup', (e) => {
      if (viewHelperRef.current) {
        viewHelperRef.current.handleClick(e);
      }
    });

    // Animation loop
    const clock = new Three.Clock();
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      const delta = clock.getDelta();

      // Update all animation mixers in the scene
      if (sceneRef.current) {
        sceneRef.current.traverse((object) => {
          if (object.userData && object.userData.mixer) {
            object.userData.mixer.update(delta);
          }
        });
      }

      if (controlsRef.current) {
        controlsRef.current.update();
      }
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
      if (viewHelperRef.current && viewHelperRendererRef.current) {
        if (viewHelperRef.current.animating) {
          viewHelperRef.current.update(delta);
        }
        viewHelperRef.current.render(viewHelperRendererRef.current);
      }
    };
    animate();

    // Load meshes from assets prop
    const loadMeshes = async () => {
      if (!assets || !sceneRef.current) return;

      try {
        const loader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
        dracoLoader.setDecoderConfig({ type: 'js' });
        loader.setDRACOLoader(dracoLoader);

        loadersRef.current = { gltfLoader: loader, dracoLoader };

        sceneRef.current.traverse((object) => {
          if (object.isMesh) {
            sceneRef.current.remove(object);
            if (object.geometry) object.geometry.dispose();
            if (object.material) {
              if (Array.isArray(object.material)) {
                object.material.forEach((material) => material.dispose());
              } else {
                object.material.dispose();
              }
            }
          }
        });

        let totalAssets = assets.length;
        let loadedAssets = 0;

        for (const meshData of assets) {
          try {
            const gltf = await new Promise((resolve, reject) => {
              loader.load(
                meshData.asset_file_path,
                (gltf) => resolve(gltf),
                (progress) => {
                  const assetProgress =
                    (progress.loaded / progress.total) * 100;
                  const totalProgress =
                    ((loadedAssets + assetProgress / 100) / totalAssets) * 100;
                  setLoadingProgress(totalProgress);
                },
                (error) => reject(error)
              );
            });

            if (!sceneRef.current) break;

            const model = gltf.scene.clone();

            // Set up animations for the cloned model
            // Note: clone() doesn't preserve animations, so we need to recreate them
            if (
              gltf &&
              Array.isArray(gltf.animations) &&
              gltf.animations.length > 0
            ) {
              try {
                const mixer = new Three.AnimationMixer(model);
                model.userData = model.userData || {};
                model.userData.mixer = mixer;
                model.userData.animationActions = [];
                // Store the animation clips for later use
                model.userData.animationClips = gltf.animations;

                gltf.animations.forEach((clip) => {
                  try {
                    const action = mixer.clipAction(clip);
                    action.reset();
                    action.setLoop(Three.LoopRepeat);
                    // Auto-play animations in preview
                    action.play();
                    model.userData.animationActions.push(action);
                  } catch (e) {
                    console.warn('Failed to create animation action', e);
                  }
                });
              } catch (e) {
                console.warn('Failed to initialize animations for model', e);
              }
            }

            const box = new Three.Box3().setFromObject(model);
            const size = box.getSize(new Three.Vector3());
            const center = box.getCenter(new Three.Vector3());

            const maxDim = Math.max(size.x, size.y, size.z);
            const MAX_ALLOWED_SIZE = 10;
            let scaleFactor = 1;

            if (maxDim > MAX_ALLOWED_SIZE) {
              scaleFactor = MAX_ALLOWED_SIZE / maxDim;
              model.scale.set(scaleFactor, scaleFactor, scaleFactor);
            }

            const userScale = new Three.Vector3(
              meshData.scale_x || 1,
              meshData.scale_y || 1,
              meshData.scale_z || 1
            );
            model.scale.multiply(userScale);

            model.position.set(
              (meshData.position_x || 0) - center.x * scaleFactor,
              (meshData.position_y || 0) - center.y * scaleFactor,
              (meshData.position_z || 0) - center.z * scaleFactor
            );

            model.rotation.set(
              ((meshData.rotation_x || 0) * Math.PI) / 180,
              ((meshData.rotation_y || 0) * Math.PI) / 180,
              ((meshData.rotation_z || 0) * Math.PI) / 180
            );

            sceneRef.current.add(model);
            loadedAssets++;
          } catch (error) {
            console.error('Error loading mesh:', meshData.file_name, error);
            setLoadingError(
              `Failed to load ${meshData.file_name}: ${error.message}`
            );
          }
        }

        if (sceneRef.current && cameraRef.current && controlsRef.current) {
          const box = new Three.Box3().setFromObject(sceneRef.current);
          if (!box.isEmpty()) {
            const center = box.getCenter(new Three.Vector3());
            const size = box.getSize(new Three.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);

            if (maxDim > 0) {
              const fov = cameraRef.current.fov * (Math.PI / 180);
              // Calculate camera distance based on model size and FOV
              const cameraDistance = Math.abs(maxDim / Math.sin(fov / 2)) * 0.2;

              // Position camera
              cameraRef.current.position.set(
                center.x + cameraDistance * 0.3,
                center.y + cameraDistance * 0.3,
                center.z + cameraDistance * 0.3
              );

              // Look at center
              cameraRef.current.lookAt(center);
              controlsRef.current.target.copy(center);

              // Set camera near and far planes based on model size
              cameraRef.current.near = maxDim * 0.001;
              cameraRef.current.far = maxDim * 100;
              cameraRef.current.updateProjectionMatrix();

              controlsRef.current.update();
            }
          }
        }
      } catch (error) {
        console.error('Error loading project data:', error);
        setLoadingError(`Failed to load project: ${error.message}`);
      }
    };

    loadMeshes();

    // Handle window resize
    const handleResize = () => {
      if (cameraRef.current && rendererRef.current) {
        cameraRef.current.aspect = window.innerWidth / window.innerHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(window.innerWidth, window.innerHeight);
      }
    };
    window.addEventListener('resize', handleResize);

    // Cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      cleanup();
    };
  }, [experienceId, assets, cleanup]);

  return (
    <div className="scene-preview rounded-[20px]">
      <div
        ref={canvasRef}
        className="canvas-container"
        style={{ position: 'static' }}
      ></div>
      {/* ViewHelper Container */}
      <div
        ref={viewHelperContainerRef}
        className="view-helper-container"
        style={{
          position: 'absolute',
          bottom: '10px',
          right: '10px',
          width: '70px',
          height: '70px',
          zIndex: 10,
        }}
      ></div>
      {experienceId && loadingProgress > 0 && loadingProgress < 100 && (
        <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <h2 className="text-white text-[24px] font-bold mb-8">
              Generating Experience
            </h2>
            <div className="relative w-52 h-3 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full rounded-full transition-all duration-300 ease-out"
                style={{
                  width: `${loadingProgress}%`,
                  backgroundImage: `repeating-linear-gradient(
                    45deg,
                    #f87171,
                    #f87171 10px,
                    #EE2B2A 10px,
                    #EE2B2A 20px
                  )`,
                  backgroundSize: '200% 100%',
                  animation: 'progressStripe 1s linear infinite',
                }}
              ></div>
            </div>
            <div className="mt-4 text-white text-lg font-medium">
              {Math.round(loadingProgress)}%
            </div>
          </div>
        </div>
      )}
      {loadingError && (
        <div className="error-overlay">
          <div className="error-message">{loadingError}</div>
        </div>
      )}
      <style jsx>{`
        .scene-preview {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
        }
        .scene-preview canvas {
          width: 100% !important;
          height: 100% !important;
        }
        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2em;
        }
        .error-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2em;
          padding: 20px;
          text-align: center;
        }
        .view-helper-container canvas {
          border-radius: 50%;
          background: #222427;
          border: 1px solid #9b9b9b;
        }
      `}</style>
    </div>
  );
};

export default React.memo(ScenePreview);
